import { useState } from "react";
import AppUtils from "@src/utils/appUtils";
import { ProfileForm } from "@src/components/forms";
import { useOperatorContext } from "@src/context/operatorContext";
import OperatorHooks from "@src/context/hooks/operatorHooks";
import { ONBOARDING_COMMITMENT_OPTIONS } from "@src/constants/constants";
import moment from "moment";
import { BackButton, Modal } from "@src/components/atoms";
import { default as SectionContainer } from "@src/pages/sharedSections/SectionContainer";
import { useIsMobileWidth } from "@src/utils/mediaQuery";
import { useAuthContext } from "@src/context/authContext";
import NotificationSettingsForm from "@src/components/forms/NotificationSettingsForm";
import NotificationSettingsFlow from "./NotificationSettingsFlow";
import ChangePasswordForm from "@src/components/forms/ChangePasswordForm";
import { trackActionActivity } from "@src/utils/userActivityTracker";
import ErrorPage from "@src/components/layout/ErrorPage";

const ProfilePage = () => {
  const { profile, setProfile, getFirstName, getLastName } =
    useOperatorContext();
  const isMobile = useIsMobileWidth();
  const auth = useAuthContext();

  const dropdownOptions = Object.entries(ONBOARDING_COMMITMENT_OPTIONS).map(
    ([key, value]) => {
      return {
        value: key,
        text: AppUtils.capitalizeFirstLetter(value?.overviewText),
      };
    },
  );

  const [changeSubmitted, setChangeSubmitted] = useState(false);
  const [showNotifsSettings, setShowNotifsSettings] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const mutationCallback = (profileData) => {
    profileData && setProfile(profileData);
    trackActionActivity(
      "NOTIFICATION_SETTINGS_EDITED",
      profileData.operatorMetadata,
    );
  };
  const mutation = OperatorHooks.useSubmitOperatorProfile(mutationCallback);

  if (!profile) return <ErrorPage pageName={"profile"} />;

  const { customer, operator, operatorMetadata } = profile;
  const commitment = operatorMetadata?.find(
    (c) => c.key === "onboarding_commitment",
  )?.["value"];
  const role = operatorMetadata?.find((c) => c.key === "role_alias")?.["value"];

  const firstName = getFirstName();
  const lastName = getLastName();

  const handleSubmit = async (data) => {
    setChangeSubmitted(false);
    const request = {
      firstname: data.firstname || firstName,
      lastname: data.lastname || lastName,
      metadata: [
        {
          key: "onboarding_commitment",
          type: "STRING",
          value: data.commitment,
        },
      ],
    };
    trackActionActivity("PROFILE_UPDATE", request);
    mutation.mutateAsync(request);
    setChangeSubmitted(true);
  };

  return (
    <section className={isMobile ? "p-2" : "p-6"}>
      <SectionContainer>
        <div className="relative flex flex-col md:items-center p-2">
          <div
            className={`${isMobile ? "w-full mb-2" : "absolute top-0 left-0"}`}
          >
            <BackButton trackingActionKey="OPERATOR_PROFILE_BACK_BUTTON" />
          </div>
          <div className={`mx-${isMobile ? 1 : 8} md:max-w-2xl`}>
            <div className=" flex-1">
              <div className="flex flex-col gap-6 mb-6 mx-auto px-6">
                <h1 className="text-2xl md:text-4xl font-extrabold text-center leading-snug capitalize break-words whitespace-normal">
                  {role} {firstName} {lastName}
                </h1>
                <div className="text-neutral-500 text-left text-sm py-4">
                  <p>
                    Joined Signol:{" "}
                    {moment.utc(operator?.enrolledDate).format("MMMM YYYY")}
                  </p>
                  <p>Operator Code: {operator?.code}</p>
                  {customer?.industry === "shipping" ? (
                    <p>Role: {role}</p>
                  ) : (
                    <p>{auth.user.username}</p>
                  )}
                </div>
              </div>
              <ProfileForm
                onProfileSubmit={handleSubmit}
                industry={"aviation"}
                defaultValues={{
                  firstname: firstName,
                  lastname: lastName,
                  commitment: commitment,
                }}
                dropdownOptions={dropdownOptions}
              ></ProfileForm>{" "}
              {changeSubmitted && (
                <div className="text-success text-sm px-6">
                  Changes saved successfully
                </div>
              )}
            </div>
            <div className="text-gray-500 text-left text-sm py-4 px-6 flex flex-col gap-8 mt-16">
              <div>
                Update your notification preferences{" "}
                <span
                  onClick={() => {
                    console.log("clicking for notif settings");
                    setShowNotifsSettings(true);
                    trackActionActivity("NOTIFICATION_SETTINGS_OPEN");
                  }}
                  className="font-bold text-primary-500 underline cursor-pointer"
                >
                  here
                </span>
              </div>
              <div>
                Change your password{" "}
                <span
                  onClick={() => {
                    console.log("clicking for password change");
                    setShowChangePassword(true);
                  }}
                  className="font-bold text-primary-500 underline cursor-pointer"
                >
                  here
                </span>
              </div>
            </div>
            {/* Modal for notifs */}
            {!!showNotifsSettings && (
              <div className="rounded-2xl overflow-y-scroll">
                <Modal
                  open={!!showNotifsSettings}
                  handleClose={() => setShowNotifsSettings(false)}
                >
                  <NotificationSettingsFlow
                    mutationCallback={mutationCallback}
                    setOpen={setShowNotifsSettings}
                  />
                </Modal>
              </div>
            )}
            {/* Modal for password change */}
            {!!showChangePassword && (
              <div>
                <Modal
                  open={!!showChangePassword}
                  handleClose={() => setShowChangePassword(false)}
                >
                  <ChangePasswordForm setOpen={setShowChangePassword} />
                </Modal>
              </div>
            )}
          </div>
        </div>
      </SectionContainer>
    </section>
  );
};

export const profilePageControls = {};

ProfilePage.propTypes = AppUtils.getPropTypeFromControls(profilePageControls);

export default ProfilePage;
